import React from 'react';
import Text from '@amzn/meridian/text';
import DisplaySubMetric from './DisplaySubMetric';
import Row from '@amzn/meridian/row';
import { FormattedMessage } from 'react-intl';
import { DisplaySubMetricsProps } from '../types';
import {
  ACHTDataCheck,
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
} from '../helper';
import { ControlledExpander } from './Util/ControlledExpander';
import Column from '@amzn/meridian/column';
import NoSubMetric from './Util/NoSubMetric';
import {
  Feature,
  isFeatureEnabled,
} from '../../../../../config/FeaturesConfig';

const DisplayACHTSubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
  vertical,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.ACHT?.submetrics) {
    return <></>;
  }
  if (ACHTDataCheck(metrics.ACHT?.submetrics!)) {
    return <NoSubMetric />;
  }

  const isGoalSettingEnabled = isFeatureEnabled(Feature.GOAL_SETTING);
  const enableHighSilence = isFeatureEnabled(Feature.HIGH_SILENCE_RATE);

  const {
    acwTime,
    acwTimeInbound,
    acwTimeOutbound,
    holdTime,
    ttcForMessageUs,
    highSilenceRate,
  } = metrics.ACHT?.submetrics;

  return (
    <Column spacing='none' width='100%'>
      {acwTime && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_averageCallWorkTime'
                  defaultMessage='Average Call Work Time'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_averageCallWorkTime_def'
                defaultMessage='Time spent in After Call Work (ACW) status per handled contact.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={acwTime.datapoint} isTime isBold />
          <DisplaySubMetric
            value={acwTime.networkAverage}
            isTime
            datapointNull={acwTime.datapoint === null}
          />
        </Row>
      )}

      {acwTimeInbound && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_afterCallWorkTimeInbound'
                  defaultMessage='After Call Work (ACW) Time - Inbound'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_averageCallWorkTime_def'
                defaultMessage='Time spent in After Call Work (ACW) status per handled contact.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={acwTimeInbound.datapoint} isTime isBold />
          <DisplaySubMetric
            value={acwTimeInbound.networkAverage}
            isTime
            datapointNull={acwTimeInbound.datapoint === null}
          />
        </Row>
      )}

      {acwTimeOutbound && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_afterCallWorkTimeOutbound'
                  defaultMessage='After Call Work (ACW) Time - Outbound'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_afterCallWorkTimeOutbound_def'
                defaultMessage='Time spent in After Call Work (ACW) status per outbound handled contact.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={acwTimeOutbound.datapoint} isTime isBold />
          <DisplaySubMetric
            value={acwTimeOutbound.networkAverage}
            isTime
            datapointNull={acwTimeOutbound.datapoint === null}
          />
        </Row>
      )}

      {holdTime && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_holdTime'
                  defaultMessage='Hold Time'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_holdTime_def'
                defaultMessage='Time spent with the customer on hold per contact handled by phone.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={holdTime.datapoint} isTime isBold />
          <DisplaySubMetric
            value={holdTime.networkAverage}
            isTime
            datapointNull={holdTime.datapoint === null}
          />
        </Row>
      )}

      {ttcForMessageUs && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_messageUsTTC'
                  defaultMessage='Message Us Time to Close'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_messageUsTTC_def'
                defaultMessage={`Time to Close (TTC) the contact after the customer's last message, per Message Us resolved contact.`}
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={ttcForMessageUs.datapoint} isTime isBold />
          <DisplaySubMetric
            value={ttcForMessageUs.networkAverage}
            isTime
            datapointNull={ttcForMessageUs.datapoint === null}
          />
        </Row>
      )}

      {enableHighSilence && isGoalSettingEnabled && highSilenceRate && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_highSilence'
                  defaultMessage='High Silence'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_highSilence_def'
                defaultMessage={`Percentage of Contacts with Silence over 60 percent for the associate.`}
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={highSilenceRate.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={highSilenceRate.networkAverage}
            isPercentage
            datapointNull={highSilenceRate.datapoint === null}
          />
        </Row>
      )}
    </Column>
  );
};

export default DisplayACHTSubMetrics;
